import { useReducedMotion } from "motion/react";
import { type RefCallback, useCallback, useEffect, useRef, useState } from "react";

export interface UseScrollSpyOptions {
  /** Ordered section keys — must match the sections' visual document order. */
  keys: readonly string[];
  /**
   * Height in px of the pinned chrome above the content (sticky strip, floating
   * bar…). Read on every recompute so it can branch on breakpoints or measured
   * element heights without re-subscribing listeners.
   */
  getTopOffset: () => number;
}

export interface UseScrollSpyReturn {
  /** Key of the section the reader is in; `null` until a section registers. */
  activeKey: string | null;
  /**
   * Whether the first section's top has reached the offset line — i.e. the
   * reader is *inside* the sections rather than above them. Drives show-on-
   * scroll chrome (e.g. a floating jump bar): unlike a viewport-top sentinel,
   * it stays `true` after a jump to the first section, which lands that
   * section exactly on the line.
   */
  pastFirstSection: boolean;
  /** Stable per-key ref callback for the section's wrapper element. */
  registerSection: (key: string) => RefCallback<HTMLElement>;
  /**
   * Jump to a section: marks it active immediately, moves focus to it (the
   * wrapper needs `tabIndex={-1}`), and scrolls it flush under the pinned
   * chrome. Smooth unless the user prefers reduced motion.
   */
  scrollToSection: (key: string) => void;
}

/** A programmatic scroll counts as settled after this long without a scroll event. */
const SETTLE_QUIET_MS = 160;
/**
 * Give up waiting for a programmatic scroll after this long — covers smooth
 * scrolls aborted without any further events (e.g. a breakpoint crossing
 * swapping the scroll container mid-flight).
 */
const SETTLE_CAP_MS = 2000;

/**
 * Tracks which of a set of in-page sections is "active" while the user scrolls,
 * and jumps between them on demand.
 *
 * The spy is a rAF-throttled, capture-phase scroll listener rather than an
 * IntersectionObserver: the active rule is "the last section whose top sits at
 * or above the offset line", which falls out of one rect read per section, needs
 * no per-breakpoint rootMargin juggling, keeps working in the gaps *between*
 * sections, and isn't throttled by iOS momentum scrolling. Capture matters —
 * scroll events don't bubble, but they do capture, so one window listener hears
 * both the document (desktop) and any inner scroll container (mobile Frame
 * scroller) without knowing which one is in play.
 *
 * The hook also owns each section's `scroll-margin-top`, keeping the value
 * `scrollIntoView` honours identical to the offset line the spy reads.
 *
 * Jumps pass an explicit scroll behavior: `html { scroll-behavior: smooth }` is
 * global (and not reduced-motion gated), so `"auto"` would still animate on
 * desktop — reduced motion therefore maps to `"instant"`, and smooth is spelled
 * out for inner scrollers that don't inherit the html rule.
 */
export function useScrollSpy({ keys, getTopOffset }: UseScrollSpyOptions): UseScrollSpyReturn {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [pastFirstSection, setPastFirstSection] = useState(false);
  const reducedMotion = useReducedMotion() ?? false;

  const sectionsRef = useRef(new Map<string, HTMLElement>());
  const refCallbacksRef = useRef(new Map<string, RefCallback<HTMLElement>>());
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const getTopOffsetRef = useRef(getTopOffset);
  getTopOffsetRef.current = getTopOffset;
  const reducedMotionRef = useRef(reducedMotion);
  reducedMotionRef.current = reducedMotion;

  // A programmatic scroll suppresses the spy so intermediate sections don't
  // flicker through the active state mid-animation.
  const suppressedRef = useRef(false);
  const quietTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameRef = useRef<number | null>(null);

  const recompute = useCallback(() => {
    frameRef.current = null;
    if (suppressedRef.current) return;

    const sections = sectionsRef.current;
    const orderedKeys = keysRef.current.filter((key) => sections.has(key));
    if (orderedKeys.length === 0) return;

    const offset = getTopOffsetRef.current();
    for (const key of orderedKeys) {
      const el = sections.get(key);
      if (el) el.style.scrollMarginTop = `${offset}px`;
    }

    // Active = the last section whose top has passed the offset line; before
    // the first section the first key stays active so the nav always points
    // somewhere sensible.
    let next = orderedKeys[0] ?? null;
    let anyPast = false;
    for (const key of orderedKeys) {
      const el = sections.get(key);
      if (el && el.getBoundingClientRect().top <= offset + 1) {
        next = key;
        anyPast = true;
      }
    }
    setPastFirstSection(anyPast);

    // Bottom edge: a short last section may never reach the offset line, so a
    // fully scrolled container force-activates it.
    const lastKey = orderedKeys[orderedKeys.length - 1];
    const firstEl = sections.get(orderedKeys[0] as string);
    if (lastKey && firstEl) {
      const container = findScrollContainer(firstEl);
      if (container.scrollTop + container.clientHeight >= container.scrollHeight - 2) next = lastKey;
    }

    setActiveKey((prev) => (prev === next ? prev : next));
  }, []);

  const scheduleRecompute = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(recompute);
  }, [recompute]);

  const settle = useCallback(() => {
    if (!suppressedRef.current) return;
    suppressedRef.current = false;
    clearTimer(quietTimerRef);
    clearTimer(capTimerRef);
    scheduleRecompute();
  }, [scheduleRecompute]);

  useEffect(() => {
    const onScroll = (event: Event) => {
      const firstEl = firstRegisteredSection(sectionsRef.current, keysRef.current);
      if (!firstEl) return;
      // Only the container our sections scroll in matters — the strip's own
      // horizontal scrolling and carousels inside sections stay ignored.
      const { target } = event;
      const isRelevant = target === document || (target instanceof Element && target.contains(firstEl));
      if (!isRelevant) return;

      if (suppressedRef.current) {
        // Mid-jump: each event pushes the quiescence deadline out.
        clearTimer(quietTimerRef);
        quietTimerRef.current = setTimeout(settle, SETTLE_QUIET_MS);
        return;
      }
      scheduleRecompute();
    };
    // A user gesture aborts an in-flight smooth scroll, after which no settle
    // signal ever arrives for the original target — hand control back at once.
    const onUserInput = () => settle();
    const onScrollEnd = () => settle();

    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    window.addEventListener("scrollend", onScrollEnd, { capture: true, passive: true });
    window.addEventListener("resize", scheduleRecompute);
    window.addEventListener("wheel", onUserInput, { passive: true });
    window.addEventListener("touchstart", onUserInput, { passive: true });
    window.addEventListener("keydown", onUserInput);

    scheduleRecompute();

    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("scrollend", onScrollEnd, { capture: true });
      window.removeEventListener("resize", scheduleRecompute);
      window.removeEventListener("wheel", onUserInput);
      window.removeEventListener("touchstart", onUserInput);
      window.removeEventListener("keydown", onUserInput);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      clearTimer(quietTimerRef);
      clearTimer(capTimerRef);
      suppressedRef.current = false;
    };
  }, [scheduleRecompute, settle]);

  const registerSection = useCallback(
    (key: string): RefCallback<HTMLElement> => {
      const cached = refCallbacksRef.current.get(key);
      if (cached) return cached;
      const callback: RefCallback<HTMLElement> = (el) => {
        if (el) {
          sectionsRef.current.set(key, el);
        } else {
          sectionsRef.current.delete(key);
        }
        scheduleRecompute();
      };
      refCallbacksRef.current.set(key, callback);
      return callback;
    },
    [scheduleRecompute],
  );

  const scrollToSection = useCallback(
    (key: string) => {
      const el = sectionsRef.current.get(key);
      if (!el) return;

      setActiveKey(key);
      // Every jump lands a section on the offset line, so the reader is
      // inside the sections regardless of where the scroll settles.
      setPastFirstSection(true);
      suppressedRef.current = true;
      clearTimer(quietTimerRef);
      quietTimerRef.current = setTimeout(settle, SETTLE_QUIET_MS);
      clearTimer(capTimerRef);
      capTimerRef.current = setTimeout(settle, SETTLE_CAP_MS);

      el.style.scrollMarginTop = `${getTopOffsetRef.current()}px`;
      // Focus first with preventScroll — a plain focus() scrolls instantly and
      // would abort the smooth scroll issued right after.
      el.focus({ preventScroll: true });
      el.scrollIntoView({ behavior: reducedMotionRef.current ? "instant" : "smooth", block: "start" });
    },
    [settle],
  );

  return { activeKey, pastFirstSection, registerSection, scrollToSection };
}

function firstRegisteredSection(sections: Map<string, HTMLElement>, keys: readonly string[]): HTMLElement | null {
  for (const key of keys) {
    const el = sections.get(key);
    if (el) return el;
  }
  return null;
}

/**
 * The element whose scrollTop moves when the sections scroll: the nearest
 * scrollable ancestor (mobile Frame scroller) or the document scroller
 * (single-column desktop).
 */
function findScrollContainer(el: HTMLElement): Element {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return document.scrollingElement ?? document.documentElement;
}

function clearTimer(ref: { current: ReturnType<typeof setTimeout> | null }) {
  if (ref.current !== null) {
    clearTimeout(ref.current);
    ref.current = null;
  }
}
